import * as React from 'react'
import { CheckCircle2, Circle, ExternalLink, Loader2 } from 'lucide-react'
import { VENUE_PAGE_DESCRIPTION_MAX, VENUE_PAGE_NEEDS, type VenuePageNeed } from '@lib/constants/venue-public-page'
import type { VenuePublicPageSettingsDto } from '@lib/dto/venue-public-page.dto'
import { updateVenuePublicPageInputSchema, type UpdateVenuePublicPageInput } from '@lib/input/venue-public-page.input'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useUpdateVenuePublicPage, useVenuePublicPage } from '@/hooks/useVenuePage'
import { Callout } from '@/components/ui/callout'
import { Checkbox } from '@/components/ui/checkbox'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { FormError } from '@/components/ui/form-error'

/** Each thing a page needs, and where to see to it. */
const NEEDS: Record<VenuePageNeed, { label: string; fix: string }> = {
  photo: { label: 'A photo', fix: 'Add one under Photo.' },
  address: {
    label: 'An address the map can find',
    fix: 'Someone who can change the venue’s address can correct it in the company’s Settings.',
  },
  description: { label: 'A description', fix: 'Write one below.' },
}

/** Which of the venue's vendors its page would list, said plainly. */
function vendorsNote(withPages: number): string {
  if (withPages === 0) return 'None of the vendors you work with have a page on csvenueview.com yet. They’re listed as they publish one.'
  const who = withPages === 1 ? '1 of the vendors you work with has' : `${String(withPages)} of the vendors you work with have`
  return `${who} a page on csvenueview.com. Only vendors with a page are listed, your recommended ones first.`
}

/** Why a page that is on isn't showing, said plainly. */
function hiddenReason(settings: VenuePublicPageSettingsDto): string | null {
  if (settings.hidden === 'missing') return 'The page is on, but it isn’t showing: it’s missing something below. It comes back by itself once that’s fixed.'
  if (settings.hidden === 'locked') return 'The page is on, but it isn’t showing while the company’s account is locked. It comes back once billing is sorted out.'
  return null
}

/**
 * A venue's page on csvenueview.com (docs/public-site.spec.md, "A venue's
 * page"), in Venue setup. Off until someone with Venue setup: Edit turns it on,
 * which needs a photo, an address the map can find and a description. The words
 * save when the box loses focus, like the rest of Venue setup.
 */
export function VenuePublicPageSection({
  venueId,
  venueName,
  canEdit,
}: {
  venueId: string
  venueName: string
  /** Venue setup: Edit: turning the page on and off, and its words. */
  canEdit: boolean
}): React.ReactElement {
  const query = useVenuePublicPage(venueId)
  const update = useUpdateVenuePublicPage(venueId)
  const [description, setDescription] = React.useState<string | null>(null)
  const [website, setWebsite] = React.useState<string | null>(null)
  const [errors, setErrors] = React.useState<{ on?: string | undefined; vendorsOn?: string | undefined; description?: string | undefined; website?: string | undefined }>({})
  // The section stays mounted when the next venue is already cached. Drop the
  // previous venue's draft on this render, before it can blur-save onto the new one.
  const [draftVenueId, setDraftVenueId] = React.useState(venueId)
  if (draftVenueId !== venueId) {
    setDraftVenueId(venueId)
    setDescription(null)
    setWebsite(null)
    setErrors({})
  }
  const venueIdRef = React.useRef(venueId)
  // A save still in flight must not clear, or report onto, what was just typed here.
  React.useLayoutEffect(() => {
    venueIdRef.current = venueId
  }, [venueId])

  if (query.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading the page…
      </p>
    )
  }
  if (query.isError) {
    return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  }

  const settings = query.data
  const shownDescription = description ?? settings.description ?? ''
  const shownWebsite = website ?? settings.website ?? ''
  const showing = settings.on && settings.hidden === null
  const reason = hiddenReason(settings)

  /** Save one change, checked with the shared rules first; `field` takes any refusal. */
  function save(input: UpdateVenuePublicPageInput, field: 'on' | 'vendorsOn' | 'description' | 'website', onSaved?: () => void): void {
    const checked = updateVenuePublicPageInputSchema.safeParse(input)
    if (!checked.success) {
      setErrors((current) => ({ ...current, [field]: checked.error.issues[0]?.message ?? 'That isn’t right' }))
      return
    }
    setErrors((current) => ({ ...current, [field]: undefined }))
    const savedFor = venueId
    update.mutate(checked.data, {
      onSuccess: () => {
        if (venueIdRef.current !== savedFor) return
        onSaved?.()
      },
      onError: (cause) => {
        if (venueIdRef.current !== savedFor) return
        setErrors((current) => ({ ...current, [field]: requestErrorMessage(cause) }))
      },
    })
  }

  function saveDescription(): void {
    if (description === null || description === (settings.description ?? '')) return
    save({ description }, 'description', () => setDescription(null))
  }

  function saveWebsite(): void {
    if (website === null || website === (settings.website ?? '')) return
    save({ website }, 'website', () => setWebsite(null))
  }

  const canTurnOn = settings.missing.length === 0

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-ink-muted">
        Give {venueName} a page on csvenueview.com, listed with the other venues there. It shows the venue’s photo, its name and address with a
        map, the description below and a link to its website. Nothing else about the venue is shown.
      </p>

      <div className="space-y-2">
        <label className="inline-flex items-center gap-2 text-sm font-medium text-ink">
          <Checkbox
            checked={settings.on}
            disabled={!canEdit || update.isPending || (!settings.on && !canTurnOn)}
            onCheckedChange={(on) => save({ on }, 'on')}
          />
          Show this venue’s page on csvenueview.com
        </label>
        {!canEdit ? <p className="text-sm text-ink-muted">Only someone who can edit Venue setup can change this.</p> : null}
        {canEdit && !settings.on && !canTurnOn ? <p className="text-sm text-ink-muted">It can be turned on once it has everything below.</p> : null}
        {errors.on === undefined ? null : <FormError>{errors.on}</FormError>}
      </div>

      {reason === null ? null : <Callout tone="attention">{reason}</Callout>}

      {showing && settings.path !== null ? (
        <a
          href={`${window.location.origin}${settings.path}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-ink underline underline-offset-2"
        >
          See the page <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      ) : null}

      <ul className="space-y-2" aria-label="What the page needs">
        {VENUE_PAGE_NEEDS.map((need) => {
          const met = !settings.missing.includes(need)
          return (
            <li key={need} className="flex items-start gap-2 text-sm">
              {met ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok-ink" aria-hidden />
              ) : (
                <Circle className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden />
              )}
              <span>
                <span className="font-medium text-ink">{NEEDS[need].label}</span>
                {met ? <span className="sr-only"> (done)</span> : <span className="text-ink-muted"> {NEEDS[need].fix}</span>}
              </span>
            </li>
          )
        })}
      </ul>

      <Field
        label="Description"
        htmlFor="venue-page-description"
        hint={`${shownDescription.length.toLocaleString('en-US')} of ${VENUE_PAGE_DESCRIPTION_MAX.toLocaleString('en-US')} characters. Blank lines start a new paragraph.`}
        error={errors.description}
      >
        <Textarea
          id="venue-page-description"
          rows={6}
          value={shownDescription}
          maxLength={VENUE_PAGE_DESCRIPTION_MAX}
          disabled={!canEdit}
          onChange={(event) => setDescription(event.target.value)}
          onBlur={saveDescription}
          placeholder="What makes the venue what it is: its setting, its spaces, the events it hosts."
        />
      </Field>

      <Field label="Website" htmlFor="venue-page-website" hint="The venue’s own site, if it has one." error={errors.website}>
        <Input
          id="venue-page-website"
          type="url"
          value={shownWebsite}
          disabled={!canEdit}
          onChange={(event) => setWebsite(event.target.value)}
          onBlur={saveWebsite}
          placeholder="https://"
        />
      </Field>

      <div className="space-y-2 border-t border-line pt-6">
        <label className="inline-flex items-center gap-2 text-sm font-medium text-ink">
          <Checkbox checked={settings.vendorsOn} disabled={!canEdit || update.isPending} onCheckedChange={(on) => save({ vendorsOn: on === true }, 'vendorsOn')} />
          List this venue’s vendors on its page
        </label>
        <p className="text-sm text-ink-muted">{vendorsNote(settings.vendorsWithPages)}</p>
        {errors.vendorsOn === undefined ? null : <FormError>{errors.vendorsOn}</FormError>}
      </div>
    </div>
  )
}
